// client/src/components/audience/SegmentBuilder.jsx
//
// /:tenant/audience/segments/:segmentId ('new' to create) — build an audience
// from AND / OR groups of rules (bought tickets, checked in, entered a
// raffle, bid in an auction, submitted a form, clicked a link, total spend,
// tags…). The count of matching people updates as you edit (debounced; the
// server caches it for 30 s). Saving stores the rules; the outreach-sender
// worker then rebuilds the member list in the background.
// Growth Phase 5, feature_key "audience_messaging".

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import {
  archiveSegment, cleanRules, countRules, countSegment, createSegment, errorText, getOptions, getSegment,
  groupOp, isGroup, isNotReady, newRule, refreshSegment, tagContacts, updateSegment,
} from '../../services/audienceApi';
import { formatCount } from '../analytics/attributionFormat';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Audience.css';

const MAX_DEPTH = 3;
const MAX_RULES = 40;
const COUNT_DEBOUNCE_MS = 450;

export function ruleLabel(t, type) {
  return {
    attended_event: t('audience.rule.attended_event', 'Checked in to an event'),
    no_show: t('audience.rule.no_show', 'Had a ticket but did not come'),
    has_ticket: t('audience.rule.has_ticket', 'Holds a ticket'),
    purchased_ticket: t('audience.rule.purchased_ticket', 'Bought tickets'),
    raffle_entered: t('audience.rule.raffle_entered', 'Entered a raffle'),
    auction_bid: t('audience.rule.auction_bid', 'Bid in an auction'),
    auction_outbid: t('audience.rule.auction_outbid', 'Was outbid in an auction'),
    contest_entered: t('audience.rule.contest_entered', 'Entered a contest'),
    form_submitted: t('audience.rule.form_submitted', 'Submitted a form'),
    clicked_link: t('audience.rule.clicked_link', 'Clicked a link'),
    total_spend_gte: t('audience.rule.total_spend_gte', 'Has spent at least'),
    email_opt_in: t('audience.rule.email_opt_in', 'Opted in to email'),
    has_push: t('audience.rule.has_push', 'Can get push notifications'),
    tag: t('audience.rule.tag', 'Has tag'),
  }[type] || type;
}

const EMPTY = { all: [] };

export default function SegmentBuilder({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);
  const segmentId = params.segmentId && params.segmentId !== 'new' ? Number(params.segmentId) : null;

  const [options, setOptions] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', isDynamic: true });
  const [rules, setRules] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [count, setCount] = useState({ state: 'idle', n: null, error: '' });
  const [notice, setNotice] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tagDraft, setTagDraft] = useState({ tag: '', emails: '' });
  const seq = useRef(0);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const opts = await getOptions(tenant);
        if (!live) return;
        setOptions(opts.data);
        if (segmentId) {
          const res = await getSegment(tenant, segmentId);
          if (!live) return;
          setSaved(res.data);
          setForm({ name: res.data.name, description: res.data.description || '', isDynamic: res.data.isDynamic });
          setRules(isGroup(res.data.rules) ? res.data.rules : EMPTY);
        }
      } catch (err) {
        if (live) {
          setLoadError(isNotReady(err)
            ? t('audience.notReady', 'Audience messaging needs a database update before it can be used.')
            : t('audience.segments.loadError', 'Could not load this segment.'));
        }
      }
    })();
    return () => { live = false; };
  }, [tenant, segmentId, t]);

  // Debounced live count. A sequence number drops answers to older edits.
  useEffect(() => {
    if (!options) return undefined;
    const mine = ++seq.current;
    setCount((c) => ({ ...c, state: 'counting', error: '' }));
    const timer = setTimeout(async () => {
      try {
        const res = await countSegment(tenant, cleanRules(rules));
        if (mine === seq.current) setCount({ state: 'done', n: res.count, error: '' });
      } catch (err) {
        if (mine === seq.current) setCount({ state: 'error', n: null, error: errorText(err, t('audience.builder.countError', 'Could not count.')) });
      }
    }, COUNT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rules, options, tenant, t]);

  const total = countRules(rules);

  const save = async (e) => {
    e.preventDefault();
    setNotice(null);
    setSaving(true);
    try {
      const body = { ...form, rules: cleanRules(rules) };
      if (segmentId) {
        const res = await updateSegment(tenant, segmentId, body);
        setSaved(res.data);
        setNotice({ kind: 'ok', text: t('audience.builder.saved', 'Saved. The member list refreshes within a few minutes.') });
      } else {
        const res = await createSegment(tenant, body);
        navigate(`/${tenant}/audience/segments/${res.data.id}`, { replace: true });
      }
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('audience.builder.saveError', 'Could not save the segment.')) });
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    try {
      await refreshSegment(tenant, segmentId);
      setNotice({ kind: 'ok', text: t('audience.builder.refreshQueued', 'The member list will be rebuilt within a few minutes.') });
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('audience.builder.refreshError', 'Could not refresh.')) });
    }
  };

  const archive = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('audience.builder.archiveConfirm', 'Archive this segment? Messages already sent keep their reports.'))) return;
    try {
      await archiveSegment(tenant, segmentId);
      navigate(`/${tenant}/audience`);
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('audience.builder.archiveError', 'Could not archive.')) });
    }
  };

  const applyTag = async (e) => {
    e.preventDefault();
    const emails = tagDraft.emails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    try {
      const res = await tagContacts(tenant, { tag: tagDraft.tag, emails });
      setNotice({ kind: 'ok', text: t('audience.tags.done', '{{n}} contacts tagged.', { n: res.tagged }) });
      setTagDraft({ tag: tagDraft.tag, emails: '' });
      const opts = await getOptions(tenant);
      setOptions(opts.data);
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('audience.tags.error', 'Could not tag those contacts.')) });
    }
  };

  if (loadError) return <div className="au-page"><div className="note error" role="alert">{loadError}</div></div>;
  if (!options) return <div className="au-page"><LoadingState /></div>;

  return (
    <div className="au-page">
      <section className="surface">
        <h2>{segmentId ? t('audience.builder.editTitle', 'Edit segment') : t('audience.builder.newTitle', 'New segment')}</h2>
        <p className="surface-muted perf-small">
          {t('audience.builder.intro', 'Add rules to pick people from your contacts. "All of" needs every rule to match; "any of" needs one. Groups can sit inside groups.')}
        </p>
      </section>

      <form className="surface" onSubmit={save}>
        <div className="fo-form-grid">
          <label className="fo-field">
            {t('audience.builder.name', 'Name')}
            <input value={form.name} required maxLength={150} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('audience.builder.namePlaceholder', 'Gala buyers who have not checked in')} />
          </label>
          <label className="fo-field">
            {t('audience.builder.description', 'Notes (staff only)')}
            <input value={form.description} maxLength={500} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <label className="fo-check">
            <input type="checkbox" checked={form.isDynamic} onChange={(e) => setForm({ ...form, isDynamic: e.target.checked })} />
            {t('audience.builder.dynamic', 'Keep up to date (recount before every message)')}
          </label>
        </div>

        <h4 style={{ marginTop: 12 }}>{t('audience.builder.rules', 'Who is in it')}</h4>
        <Group
          node={rules}
          depth={0}
          options={options}
          canAdd={total < MAX_RULES}
          onChange={setRules}
        />

        <div className="au-count" aria-live="polite" style={{ marginTop: 10 }}>
          {count.state === 'counting' && t('audience.builder.counting', 'Counting…')}
          {count.state === 'error' && <span role="alert">{count.error}</span>}
          {count.state === 'done' && (
            <>
              <span className="au-count-n">{formatCount(count.n)}</span>{' '}
              {t('audience.builder.matches', 'people match right now')}
            </>
          )}
        </div>
        {saved && (
          <p className="surface-muted perf-small">
            {t('audience.builder.lastComputed', 'Member list: {{n}} people, last built {{when}}.', {
              n: saved.cachedCount === null ? '—' : formatCount(saved.cachedCount),
              when: saved.lastComputedOn || t('audience.segments.notYet', 'Not yet'),
            })}
          </p>
        )}

        <div className="au-actions" style={{ marginTop: 10 }}>
          <button type="submit" className="lm-btn" disabled={saving || !form.name.trim()}>
            {saving ? t('audience.saving', 'Saving…') : t('audience.builder.save', 'Save segment')}
          </button>
          {segmentId && (
            <>
              <button type="button" className="lm-btn secondary" onClick={refresh}>{t('audience.builder.refresh', 'Rebuild member list')}</button>
              <button type="button" className="lm-btn secondary" onClick={() => navigate(`/${tenant}/audience/messages/new?segment=${segmentId}`)}>
                {t('audience.builder.message', 'Message this segment')}
              </button>
              <button type="button" className="lm-btn secondary" onClick={archive}>{t('audience.builder.archive', 'Archive')}</button>
            </>
          )}
        </div>
        {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
      </form>

      <form className="surface" onSubmit={applyTag}>
        <h4>{t('audience.tags.title', 'Tag contacts')}</h4>
        <p className="surface-muted perf-small">
          {t('audience.tags.help', 'Paste email addresses of people who are already contacts (they bought, entered or filled in a form), then use the "Has tag" rule.')}
        </p>
        <div className="fo-form-grid">
          <label className="fo-field">
            {t('audience.tags.tag', 'Tag')}
            <input value={tagDraft.tag} maxLength={64} required list="au-tag-list"
              onChange={(e) => setTagDraft({ ...tagDraft, tag: e.target.value })} />
          </label>
          <label className="fo-field">
            {t('audience.tags.emails', 'Email addresses (up to 500)')}
            <textarea rows={3} value={tagDraft.emails} required onChange={(e) => setTagDraft({ ...tagDraft, emails: e.target.value })} />
          </label>
          <div className="fo-field" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="lm-btn secondary">{t('audience.tags.apply', 'Add tag')}</button>
          </div>
        </div>
        <datalist id="au-tag-list">{(options.tags || []).map((g) => <option key={g.tag} value={g.tag} />)}</datalist>
      </form>

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(`/${tenant}/audience`) },
          { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group / rule editors
// ---------------------------------------------------------------------------

function Group({ node, depth, options, canAdd, onChange, onRemove }) {
  const { t } = useTranslation();
  const op = groupOp(node);
  const kids = node[op];
  const [adding, setAdding] = useState('');

  const setKids = useCallback((next) => onChange({ [op]: next }), [onChange, op]);
  const setOp = (nextOp) => onChange({ [nextOp]: kids });
  const add = (type) => {
    if (!type) return;
    setKids([...kids, newRule(type)]);
    setAdding('');
  };

  return (
    <div className={`au-group ${op}`} role="group" aria-label={op === 'all' ? t('audience.builder.groupAll', 'Match all of') : t('audience.builder.groupAny', 'Match any of')}>
      <div className="au-group-head">
        <label>
          <span className="visually-hidden">{t('audience.builder.groupMode', 'Group mode')}</span>
          <select value={op} onChange={(e) => setOp(e.target.value)} aria-label={t('audience.builder.groupMode', 'Group mode')}>
            <option value="all">{t('audience.builder.groupAll', 'Match all of')}</option>
            <option value="any">{t('audience.builder.groupAny', 'Match any of')}</option>
          </select>
        </label>
        {kids.length === 0 && (
          <span className="perf-small">
            {op === 'all' ? t('audience.builder.emptyAll', '(no rules: everyone)') : t('audience.builder.emptyAny', '(no rules: no one)')}
          </span>
        )}
        {onRemove && (
          <button type="button" className="lm-btn secondary" onClick={onRemove}>{t('audience.builder.removeGroup', 'Remove group')}</button>
        )}
      </div>

      {kids.map((child, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="au-joiner">{op === 'all' ? t('audience.builder.and', 'and') : t('audience.builder.or', 'or')}</div>}
          {isGroup(child) ? (
            <Group
              node={child}
              depth={depth + 1}
              options={options}
              canAdd={canAdd}
              onChange={(next) => setKids(kids.map((k, j) => (j === i ? next : k)))}
              onRemove={() => setKids(kids.filter((_, j) => j !== i))}
            />
          ) : (
            <Rule
              rule={child}
              options={options}
              onChange={(next) => setKids(kids.map((k, j) => (j === i ? next : k)))}
              onRemove={() => setKids(kids.filter((_, j) => j !== i))}
            />
          )}
        </React.Fragment>
      ))}

      <div className="au-group-head">
        <label>
          <span className="visually-hidden">{t('audience.builder.addRule', 'Add a rule')}</span>
          <select value={adding} disabled={!canAdd} aria-label={t('audience.builder.addRule', 'Add a rule')}
            onChange={(e) => add(e.target.value)}>
            <option value="">{t('audience.builder.addRule', 'Add a rule')}…</option>
            {options.ruleTypes.map((r) => <option key={r.type} value={r.type}>{ruleLabel(t, r.type)}</option>)}
          </select>
        </label>
        {depth < MAX_DEPTH && (
          <button type="button" className="lm-btn secondary" disabled={!canAdd}
            onClick={() => setKids([...kids, { [op === 'all' ? 'any' : 'all']: [] }])}>
            {t('audience.builder.addGroup', 'Add group')}
          </button>
        )}
      </div>
    </div>
  );
}

function Rule({ rule, options, onChange, onRemove }) {
  const { t } = useTranslation();
  const def = options.ruleTypes.find((r) => r.type === rule.type) || { params: [] };
  const set = (k, v) => onChange({ ...rule, [k]: v });
  const num = (v) => (v === '' ? '' : Number(v));
  const tiers = options.tiers.filter((x) => !rule.eventId || x.eventId === Number(rule.eventId));

  const field = (key) => {
    switch (key) {
      case 'eventId':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.event', 'Event')}
            <select value={rule.eventId || ''} onChange={(e) => onChange({ ...rule, eventId: num(e.target.value), tierId: '' })}>
              <option value="">{t('audience.param.anyEvent', 'Any event')}</option>
              {options.events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </label>
        );
      case 'tierId':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.tier', 'Ticket type')}
            <select value={rule.tierId || ''} onChange={(e) => set('tierId', num(e.target.value))}>
              <option value="">{t('audience.param.anyTier', 'Any ticket type')}</option>
              {tiers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </label>
        );
      case 'status':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.status', 'Ticket status')}
            <select value={rule.status || ''} onChange={(e) => set('status', e.target.value)}>
              <option value="">{t('audience.param.anyStatus', 'Any status')}</option>
              {options.ticketStatuses.map((s) => <option key={s} value={s}>{t(`audience.ticketStatus.${s}`, s.replace('_', ' '))}</option>)}
            </select>
          </label>
        );
      case 'formId':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.form', 'Form')}
            <select value={rule.formId || ''} onChange={(e) => set('formId', num(e.target.value))}>
              <option value="">{t('audience.param.anyForm', 'Any form')}</option>
              {options.forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>
        );
      case 'linkId':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.link', 'Tracking link')}
            <select value={rule.linkId || ''} onChange={(e) => onChange({ ...rule, linkId: num(e.target.value), sendId: '' })}>
              <option value="">{t('audience.param.anyLink', 'Any link or email')}</option>
              {options.links.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </label>
        );
      case 'sendId':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.email', 'In this email')}
            <select value={rule.sendId || ''} onChange={(e) => onChange({ ...rule, sendId: num(e.target.value), linkId: '' })}>
              <option value="">{t('audience.param.anyEmail', 'Any email')}</option>
              {options.emailSends.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        );
      case 'withinDays':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.withinDays', 'In the last … days (blank = ever)')}
            <input type="number" min={1} max={3650} value={rule.withinDays ?? ''} onChange={(e) => set('withinDays', num(e.target.value))} />
          </label>
        );
      case 'amount':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.amount', 'Amount')}
            <input type="number" min={0} step="0.01" required value={rule.amount ?? ''} onChange={(e) => set('amount', num(e.target.value))} />
          </label>
        );
      case 'currency':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.currency', 'Currency (blank = all)')}
            <input maxLength={3} value={rule.currency || ''} onChange={(e) => set('currency', e.target.value.toLowerCase())} placeholder="usd" />
          </label>
        );
      case 'tag':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.tag', 'Tag')}
            <input value={rule.tag || ''} maxLength={64} required list="au-tag-list" onChange={(e) => set('tag', e.target.value)} />
          </label>
        );
      case 'value':
        return (
          <label className="fo-field" key={key}>
            {t('audience.param.optedIn', 'Opted in')}
            <select value={rule.value === false ? 'no' : 'yes'} onChange={(e) => set('value', e.target.value === 'yes')}>
              <option value="yes">{t('audience.param.yes', 'Yes')}</option>
              <option value="no">{t('audience.param.no', 'No')}</option>
            </select>
          </label>
        );
      default:
        return null;
    }
  };

  return (
    <div className="au-rule">
      <div className="au-rule-head">
        <span>{rule.negate ? `${t('audience.builder.not', 'Not')}: ` : ''}{ruleLabel(t, rule.type)}</span>
        <span className="au-actions">
          <label className="fo-check">
            <input type="checkbox" checked={!!rule.negate} onChange={(e) => set('negate', e.target.checked)} />
            {t('audience.builder.negate', 'Exclude these people')}
          </label>
          <button type="button" className="lm-btn secondary" onClick={onRemove}>{t('audience.builder.remove', 'Remove')}</button>
        </span>
      </div>
      {def.params.length > 0 && <div className="au-rule-params">{def.params.map(field)}</div>}
    </div>
  );
}
